import {
  Typography,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Stack,
  Paper,
  TextField,
  Button,
  ButtonBase,
  FormControl,
} from "@mui/material";
import LogMeetingButton from "../../components/meetings/LogMeetingButton";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import API from "../../api/axiosConfig";

export default function MeetingDisplay() {
  const { user, isAdmin, loading: authLoading } = useUser();

  const [timeframe, setTimeFrame] = useState("ytd");
  const [meetings, setMeetings] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const navigate = useNavigate();

  // Format for US currency
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  /**
   * Asynchronously gathers meeting data from the backend API based on the current timeframe.
   * Handles authentication, response parsing, and error conditions.
   */
  const gatherMeetings = useCallback(
    async (customStartDate = null, customEndDate = null) => {
      // Note: We don't need to manually check 'if (!token)' here if our
      // interceptor handles the 401 redirect globally.
      let requestBody = { timeframe: timeframe };

      if (timeframe === "custom" && customStartDate && customEndDate) {
        requestBody.startDate = customStartDate;
        requestBody.endDate = customEndDate;
      }

      if (timeframe === "custom" && !customStartDate && customEndDate) {
        return;
      }

      try {
        const response = await API.post("/api/meetings/getall", requestBody);
        const fetchedMeetings = response.data || [];

        setMeetings(fetchedMeetings);

        const calculatedTotalCost = fetchedMeetings.reduce((sum, meeting) => {
          return (
            sum +
            (typeof meeting.amountSpent === "number" ? meeting.amountSpent : 0)
          );
        }, 0);
        setTotalCost(calculatedTotalCost);
      } catch (err) {
        // Remember: The interceptor handles the 401 redirect to '/' automatically!
        // We only alert for other issues.
        if (err.response?.status !== 401) {
          alert("Error during meeting fetch! Check console for more details.");
          console.error("Error during meeting fetch:", err);
        }
        setMeetings([]);
        setTotalCost(0);
      }
    },
    [timeframe],
  );

  /**
     * Handles the deletion of a specific meeting.
     * @param {string} meetingId The ID of the meeting to be deleted.
     
    const handleDeleteMeeting = async (meetingId) => {
        if (!token) {
            console.warn('No authentication token found. Cannot delete meeting.');
            alert('Please log in to delete meetings.');
            return;
        }


        // Find the meeting object by ID to check its parent
        const meetingToDelete = meetings.find(m => m.id === meetingId);

        // Check if the user is the parent of the meeting
        if(meetingToDelete === null){
            alert('Error sensing which meeting to delete!');
            return;
        }
        if (meetingToDelete.parent !== userEmail && !admin) {
            alert('You do not have permission to delete this meeting.');
            console.warn('Attempted to delete a meeting without permission.');
            return;
        }

        // Confirm with the user for meeting deletion. Start process if approved.
        if (window.confirm('Are you sure you want to delete this meeting?')) {
            // Indicate loading while deleting
            setLoading(true); 
            try {
                const response = await API.post('/api/meetings/delete', {
                    meetingId: meetingId,
                });

                if (response.data === true) {
                    // Re-fetch meetings to update the displayed list and total cost
                    if (timeframe === 'custom' && startDate && endDate) {
                        gatherMeetings(startDate, endDate);
                    } else {
                        gatherMeetings();
                    }
                    alert("The meeting has been deleted.");
                    return;
                } else {
                    // Incase 200 is returned by the server, but returns false
                    // Insufficient permissions or no meeting exists
                    alert("Error: You do not have permission to delete this meeting!");
                    console.log("Error: Meeting does not exist or insufficient permissions to delete meeting.");
                }
            } catch (error) {
                // Don't log users out on 403 (Forbidden). 
                // Only the interceptor/401 logic should handle logouts.
                if (error.response?.status === 403) {
                    // Insufficient permissions or no meeting exists
                    alert("Error: You do not have permission to delete this meeting!");
                } else if (error.response?.status !== 401) {
                    // Other, unexpected error handling
                    alert('Error deleting meeting! Check console for details.');
                    console.error('Error deleting meeting:', error);
                }

            // Finish loading
            } finally {
                setLoading(false);
            }
        }
    };
    */

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await gatherMeetings();
      } catch (error) {
        console.error("Failed to load all data:", error);
      } finally {
        setLoading(false);
      }
    };

    // Guard execution until context session resolves
    if (!authLoading && user) {
      if (timeframe !== "custom") {
        fetchData();
      }
    }
  }, [timeframe, user, authLoading, gatherMeetings]);

  const handleChange = (event) => {
    setTimeFrame(event.target.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleCustomSearch = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Set the end date to the end of the day for proper range filtering
    // This is important because the date input only gives YYYY-MM-DD
    // So, if end date is '2023-10-26', it will be 2023-10-26 00:00:00
    // We want to include meetings *on* the end date.
    end.setHours(23, 59, 59, 999);

    if (start > end) {
      alert("Start Date cannot be later than End Date.");
      return;
    }

    if (timeframe === "custom") {
      gatherMeetings(startDate, endDate);
    }
  };

  /////////////////
  // MAIN RETURN //
  /////////////////
  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        sx={{ margin: "16px", textAlign: "center", marginBottom: "32px" }}
      >
        Meeting Log
      </Typography>

      <Box sx={{ mb: 4 }}>
        {/* Controls Bar: Stacks vertically on mobile, horizontally on desktop */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ mb: 2 }}
        >
          {isAdmin && (
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                px: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Total Cost:
              </Typography>
              <Typography variant="subtitle1" sx={{ ml: 1 }}>
                {formatter.format(totalCost)}
              </Typography>
            </Paper>
          )}

          {/* Timeframe Dropdown (Defined only once) */}
          <FormControl sx={{ minWidth: { xs: "100%", sm: 190 } }}>
            <InputLabel id="timeframe-select-label">Timeframe</InputLabel>
            <Select
              labelId="timeframe-select-label"
              id="timeframe-select"
              value={timeframe}
              label="Timeframe"
              onChange={handleChange}
              disabled={loading}
            >
              <MenuItem value="ytd">Year-to-Date</MenuItem>
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="month">Month</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="year">Year</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
              <MenuItem value="alltime">All Time</MenuItem>
            </Select>
          </FormControl>

          <LogMeetingButton />
        </Stack>

        {/* Custom Date Inputs (Defined only once) */}
        {timeframe === "custom" && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 2 }}
          >
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              disabled={loading}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: { xs: "100%", sm: 200 } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              disabled={loading}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: { xs: "100%", sm: 200 } }}
            />
            <Button
              variant="contained"
              onClick={handleCustomSearch}
              disabled={loading || !startDate || !endDate}
              sx={{
                width: { xs: "100%", sm: "auto" },
                px: 4,
                height: 40,
              }}
            >
              Search
            </Button>
          </Stack>
        )}
      </Box>

      {/* //////////////////////////// */}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Typography variant="body1" sx={{ ml: 2, alignSelf: "center" }}>
            <i>Loading meetings within timeframe...</i>
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            mt: 4,
            mb: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          {meetings.length > 0 ? (
            meetings.map((meeting) => (
              <ButtonBase
                onClick={() => navigate(`/meetings/${meeting.id}`)}
                sx={{
                  width: "80%",
                  maxWidth: 600,
                  borderRadius: 1, 
                  overflow: "hidden", 
                  mb: 2, 
                }}
                key={meeting.id}
              >
                <Paper
                  key={meeting.id}
                  elevation={3}
                  sx={(theme) => ({
                    p: 2,
                    width: "100%",
                    maxWidth: 600,
                    position: "relative", // Needed for absolute positioning of the icon

                    // Initial states
                    outline: "10px solid transparent",
                    backgroundColor: theme.palette.paper, // Standard theme background

                    // Multi-property transition for a smooth effect
                    transition:
                      "outline-color 0.3s ease-in-out, background-color 0.3s ease-in-out",

                    "&:hover": {
                      // Hover states
                      backgroundColor: theme.palette.paper.dark,
                      cursor: "pointer",
                    },
                  })}
                >
                  <Stack spacing={1}>
                    <Typography variant="body1">
                      <strong>Location:</strong>{" "}
                      {meeting.location || "N/A"}{" "}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Time:</strong>{" "}
                      {new Date(meeting.time).toLocaleString()}
                    </Typography>
                    {isAdmin ? (
                      <Typography variant="body2">
                        <strong>Amount Spent: </strong>
                        {meeting.amountSpent !== null &&
                        meeting.amountSpent !== undefined
                          ? formatter.format(meeting.amountSpent)
                          : "0.00"}{" "}
                      </Typography>
                    ) : null}
                    {meeting.description && (
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        <strong>Description:{"\n"}</strong>{" "}
                        {meeting.description}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </ButtonBase>
            ))
          ) : (
            <Typography variant="body1" sx={{ mt: 4 }}>
              No meetings found for the selected timeframe.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
